import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Box,
  useToast,
  Icon,
} from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import CSSAvatar from "./CSSAvatar";
import { crud } from "../crudRequests";

export default function GroupManagementModal({
  isOpen,
  onClose,
  group,
  currentUserId,
  patient,
  dashState,
  onLeaveGroup,
}) {
  const toast = useToast();
  const [isLeaving, setIsLeaving] = useState(false);
  
  if (!group) return null;
  
  // Check if current user is admin
  const isCurrentUserAdmin = group.members?.[currentUserId]?.isAdmin || 
                            group.members?.[currentUserId]?.role === "owner";
  
  // Sort members: owner first, then admins, then regular members
  const sortedMembers = Object.entries(group.members || {}).sort(([idA, memberA], [idB, memberB]) => {
    if (memberA.role === 'owner') return -1;
    if (memberB.role === 'owner') return 1;
    if (memberA.isAdmin && !memberB.isAdmin) return -1;
    if (!memberA.isAdmin && memberB.isAdmin) return 1;
    return 0;
  });
  
  // Leave group handler
  const handleLeaveGroup = async () => {
    setIsLeaving(true);
    
    try {
      const updatedUsersMeta = { ...group.members };
      delete updatedUsersMeta[currentUserId];
      
      const updatedUsersId = group.usersId.filter(id => id !== currentUserId);
      
      // Update conversation and remove from patient's chatsId
      const requests = [
        {
          db: dashState.db || dashState.dbName || "blh",
          collection: "conversations",
          parameters: [
            { conversationId: group.conversationId },
            {
              $set: { usersMeta: updatedUsersMeta },
              $pull: { usersId: currentUserId }
            }
          ],
          method: "updateOne",
        },
        {
          db: dashState.db || dashState.dbName || "blh",
          collection: "patients",
          parameters: [
            { pid: currentUserId },
            { $pull: { chatsId: group.conversationId } }
          ],
          method: "updateOne",
        }
      ];
      
      await crud(dashState, requests, {
        socket: {
          rooms: Object.keys(group.members || {})
            .filter(id => id !== currentUserId)
            .map(id => `user_${id}`),
          namespace: "/chat",
          arguments: ["group-member-left", {
            groupId: group.conversationId,
            leftMemberId: currentUserId,
            memberName: group.members?.[currentUserId]?.name || `${patient?.fName || ''} ${patient?.lName || ''}`.trim() || "A member"
          }],
        },
      });
      
      toast({
        title: "Left group successfully",
        status: "success",
        duration: 3000,
      });
      
      // Call parent handler to update UI
      if (onLeaveGroup) {
        onLeaveGroup(group.conversationId);
      }
      
      onClose();
    } catch (error) {
      console.error("Error leaving group:", error);
      toast({
        title: "Failed to leave group",
        description: "Please try again",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLeaving(false);
    }
  };
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="md"
      isCentered
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.4)" />
      <ModalContent>
        <ModalHeader 
          fontSize="18px" 
          fontWeight="600" 
          color="gray.900"
          borderBottom="1px solid"
          borderColor="gray.200"
          pb={3}
        >
          <HStack justify="space-between" align="center">
            <Text>Manage Group: {group.groupName || "Care Group"}</Text>
            <ModalCloseButton 
              position="relative"
              right={0}
              top={0}
              size="sm"
            />
          </HStack>
        </ModalHeader>
        
        <ModalBody py={5}>
          {/* Current Members Section */}
          <Box>
            <Text fontSize="14px" color="gray.700" fontWeight="500" mb={4}>
              Current Members ({Object.keys(group.members || {}).length})
            </Text>
            
            <VStack spacing={0} align="stretch">
              {sortedMembers.map(([id, member]) => {
                const isCurrentUser = id === currentUserId;
                const isPatient = id === patient?.pid;
                const isOwner = member.role === 'owner';
                const isAdmin = member.isAdmin || isOwner;
                
                return (
                  <HStack 
                    key={id} 
                    py={3}
                    px={4}
                    bg="white"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                    transition="background 0.15s ease"
                  >
                    <CSSAvatar user={member} size="sm" />
                    <VStack align="start" spacing={0} flex={1} ml={3}>
                      <HStack spacing={2}>
                        <Text fontSize="14px" fontWeight="500" color="gray.900">
                          {member.name} {isCurrentUser && "(You)"}
                        </Text>
                        {isOwner && (
                          <Badge 
                            bg="gray.800" 
                            color="white" 
                            fontSize="10px" 
                            px={2} 
                            py={0.5}
                            borderRadius="4px"
                            fontWeight="600"
                            textTransform="uppercase"
                          >
                            OWNER
                          </Badge>
                        )}
                        {!isOwner && isAdmin && (
                          <Badge 
                            bg="gray.600" 
                            color="white" 
                            fontSize="10px" 
                            px={2} 
                            py={0.5}
                            borderRadius="4px"
                            fontWeight="600"
                            textTransform="uppercase"
                          >
                            ADMIN
                          </Badge>
                        )}
                        {isPatient && (
                          <Badge 
                            bg="green.500" 
                            color="white" 
                            fontSize="10px" 
                            px={2} 
                            py={0.5}
                            borderRadius="4px"
                            fontWeight="600"
                            textTransform="uppercase"
                          >
                            PATIENT
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="13px" color="gray.500" textTransform="capitalize">
                        {member.type === 'doctor' ? 'Clinician' : member.type}
                      </Text>
                    </VStack>
                  </HStack>
                );
              })}
            </VStack>
            
            {!isCurrentUserAdmin && (
              <Text fontSize="13px" color="gray.500" textAlign="center" mt={5}>
                Only group admins can add new members
              </Text>
            )}
          </Box>
        </ModalBody>
        
        <ModalFooter borderTop="1px solid" borderColor="gray.200" pt={4} justifyContent="space-between">
          <Button
            color="red.500"
            variant="ghost"
            onClick={handleLeaveGroup}
            fontSize="14px"
            fontWeight="500"
            _hover={{ bg: "red.50" }}
            isLoading={isLeaving}
            loadingText="Leaving..."
          >
            Leave Group
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            fontSize="14px"
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}